import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { useCurrentUserData } from "hooks/useUserData";
import { EditButtonLocationSuperAdminDialog } from "./EditButtonLocationSuperAdminDialog";
import { useDataGrid } from "hooks/useDataGrid";
import { GlobalContext } from "store/globalContext";
import { DataGrid } from "UI/DataGrid";
import { TextFieldWithEllipsis } from "UI/TextFiledWithEllipsis";
import { convertDeviceType } from "utils/helpers";
import { CRITICAL_BATTERY_LEVEL } from "models/constants";

const columns = [
  {
    label: "Button ID",
    field: "id",
    xs: 1.75,
    sm: 1.75,
    md: 1.75,
    renderer: ({ id, deviceType }) => (
      <TextFieldWithEllipsis>
        {["Virtual", "Soft", "Digital"].includes(deviceType)
          ? `${String(id).slice(0, 8)}...`
          : id}
      </TextFieldWithEllipsis>
    ),
  },
  {
    label: "Name",
    field: "buttonName",
    xs: 1.25,
    sm: 1.25,
    md: 1.25,
    renderer: ({ buttonName }) => (
      <TextFieldWithEllipsis>{buttonName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Type",
    field: "deviceType",
    xs: 0.75,
    sm: 0.75,
    md: 0.75,
    renderer: ({ deviceType }) => convertDeviceType(deviceType),
  },
  {
    label: "Practice",
    field: "practiceName",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ practiceName }) => (
      <TextFieldWithEllipsis>{practiceName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Region",
    field: "practiceRegion",
    xs: 1,
    sm: 1,
    md: 1,
    renderer: ({ practiceRegion }) => (
      <TextFieldWithEllipsis>{practiceRegion}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Sender",
    field: "senders",
    xs: 1.5,
    sm: 1.5,
    md: 1.5,
    renderer: ({ senders }) => (
      <TextFieldWithEllipsis>{senders}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Recipient",
    field: "recipientName",
    xs: 1.5,
    sm: 1.5,
    md: 1.5,
    renderer: ({ recipientName }) => (
      <TextFieldWithEllipsis>{recipientName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Specialists",
    field: "specialists",
    xs: 1.5,
    sm: 1.5,
    md: 1.5,
    renderer: ({ specialists }) => (
      <TextFieldWithEllipsis>{specialists}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Battery",
    field: "lastBatteryLevel",
    align: "center",
    xs: 0.75,
    sm: 0.75,
    md: 0.75,
    renderer: ({ lastBatteryLevel, deviceType }) =>
      deviceType === "Virtual" ? null : (
        <div
          className={
            lastBatteryLevel <= CRITICAL_BATTERY_LEVEL
              ? "text-red-700"
              : "text-gray-400"
          }
        >
          {lastBatteryLevel ? `${lastBatteryLevel}%` : "N/A"}
        </div>
      ),
  },
];

const mobileColumns = [
  {
    label: "Button ID",
    field: "id",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ id, deviceType }) => (
      <TextFieldWithEllipsis>
        {["Virtual", "Soft", "Digital"].includes(deviceType)
          ? `${String(id).slice(0, 8)}...`
          : id}
      </TextFieldWithEllipsis>
    ),
  },
  {
    label: "Name",
    field: "buttonName",
    xs: 1.5,
    sm: 1.5,
    md: 1.5,
    renderer: ({ buttonName }) => (
      <TextFieldWithEllipsis>{buttonName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Type",
    field: "deviceType",
    xs: 1.5,
    sm: 1.5,
    md: 1.5,
    renderer: ({ deviceType }) => convertDeviceType(deviceType),
  },
  {
    label: "Practice",
    field: "practiceName",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ practiceName }) => (
      <TextFieldWithEllipsis>{practiceName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Sender",
    field: "senders",
    xs: 3,
    sm: 3,
    md: 3,
    renderer: ({ senders }) => (
      <TextFieldWithEllipsis>{senders}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Recipient",
    field: "recipientName",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ recipientName }) => (
      <TextFieldWithEllipsis>{recipientName}</TextFieldWithEllipsis>
    ),
  },
];

export const AllButtons = () => {
  const navigate = useNavigate();
  const { isReadOnlyAccess } = useCurrentUserData();
  const { userData } = useContext(GlobalContext);
  const [currentButtonId, setCurrentButtonId] = useState("");

  const dataGrid = useDataGrid({
    url: "/all_button_locations",
    defaultSorter: {
      name: "practiceName",
      order: "asc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-AllButtons`,
  });
  const handleRowClick = ({ id }) =>
    isReadOnlyAccess
      ? navigate(`/button_location/${id}`)
      : setCurrentButtonId(id);

  return (
    <CommonOutletTemplate title="Buttons" isLoading={!dataGrid?.rows}>
      <DataGrid
        columns={columns}
        mobileColumns={mobileColumns}
        handleRowClick={handleRowClick}
        {...dataGrid}
      />
      {currentButtonId && (
        <EditButtonLocationSuperAdminDialog
          buttonId={currentButtonId}
          handleClose={() => setCurrentButtonId("")}
          refreshList={() => dataGrid.loadData()}
        />
      )}
    </CommonOutletTemplate>
  );
};
